import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Fragment, useEffect } from 'react';
import {
  Footer,
  StyledCheckboxTerms,
  TableCellGroupForInstallment,
  TableCellStyledForInstallment,
  TableFooterBoxStyled,
} from './styles';

import { PixRounded } from '@mui/icons-material';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';

import { formatDateSimple } from 'utils/formatDate';
import { amountFormatter } from 'utils/formatters';
import { InstallmentStatusEnum } from 'v3/data/store/installments/enum';
import { InstallmentsModal } from 'v3/shared/components/Modal/InstallmentsToPayModal';
import { PixDisplayModeModal } from 'v3/shared/components/Modal/PixDisplayModeModal';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';

import { useParams } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { InstallmentToPayMobileTable } from 'v3/shared/components/MobileTable/InstallmentsToPayMobileTable';
import { PixQRCodeModal } from 'v3/shared/components/Modal/PixQRCodeModal';
import { PixSMSConfirmationModal } from 'v3/shared/components/Modal/PixSMSConfirmationModal';
import { SuccessfulPaymentModal } from 'v3/shared/components/Modal/SuccessfulPaymentModal';
import { UnsuccessfulPaymentModal } from 'v3/shared/components/Modal/UnsuccessfulPaymentModal';
import {
  IContractingDetailsParams,
  InstallmentViewProps,
} from 'v3/shared/components/Table/InstallmentsToPayTable/interfaces';
import { useInstallmentsLogic } from 'v3/shared/components/Table/InstallmentsToPayTable/logic';
import { PaperStyled, TableRowStyled } from 'v3/shared/components/Table/styles';

const getStatusLabel = (status: InstallmentStatusEnum) => {
  switch (status) {
    case InstallmentStatusEnum.PAID:
      return 'Paga';
    case InstallmentStatusEnum.PENDING_PAYMENT:
      return 'A vencer';
    case InstallmentStatusEnum.LATE:
      return 'Atrasada';
    case InstallmentStatusEnum.CANCELED:
      return 'Cancelada';
    default:
      return 'Desconhecido';
  }
};

const DesktopInstallmentView: React.FC<InstallmentViewProps> = ({
  selectedInstallments,
  unpaidItems,
  item,
  itemsToPay,
  isModalOpen,
  handleCheckboxChange,
  canInstallmentBeToggled,
  handleOpenModal,
  handleCloseModal,
  getStatusLabel,
  isPixDisplayModeModalOpen,
  handleClosePixDisplayModeModal,
  handleConfirmInstallmentsAndOpenPix,
  handleConfirmPixSendingSMS,
  isPixSMSConfirmationModalOpen,
  handleClosePixSendingSMS,
  handleConfirmPixQRCode,
  isPixQRCodeModalOpen,
  handleClosePixQRCode,
  isSuccessfulPaymentModalOpen,
  handleCloseSuccessfulPaymentModal,
  isUnsuccessfulPaymentModalOpen,
  handleCloseUnsuccessfulPaymentModal,
  handleOpenSuccessfulPaymentModal,
}) => {
  return (
    <Fragment>
      <PaperStyled>
        <TableContainer sx={{ maxHeight: 450 }}>
          <TableMui>
            <TableHead>
              <TableRow>
                <TableCellStyledForInstallment>Parcela</TableCellStyledForInstallment>
                <TableCellStyledForInstallment>Data de vencimento</TableCellStyledForInstallment>
                <TableCellStyledForInstallment>Valor da parcela</TableCellStyledForInstallment>
                <TableCellStyledForInstallment>Valor atualizado</TableCellStyledForInstallment>
                <TableCellStyledForInstallment>Status</TableCellStyledForInstallment>
              </TableRow>
            </TableHead>

            <TableBody>
              {unpaidItems.map((row) => (
                <TableRowStyled key={row.number}>
                  <TableCellGroupForInstallment
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '1rem',
                      paddingRight: '0.5rem',
                    }}
                  >
                    <StyledCheckboxTerms
                      checked={selectedInstallments.includes(row.number)}
                      onChange={() => handleCheckboxChange(row.number)}
                      inputProps={{ 'aria-label': `selecionar parcela ${row.number}` }}
                      disabled={!canInstallmentBeToggled(row)}
                    />
                    <SubtitleMedium>{`${String(row.number).padStart(2, '0')} de ${String(item.installments.length).padStart(2, '0')}`}</SubtitleMedium>
                  </TableCellGroupForInstallment>
                  <TableCellGroupForInstallment>
                    <SubtitleMedium>{formatDateSimple(row.dueDate)}</SubtitleMedium>
                  </TableCellGroupForInstallment>
                  <TableCellGroupForInstallment>
                    <SubtitleMedium>{amountFormatter(row.amount)}</SubtitleMedium>
                  </TableCellGroupForInstallment>
                  <TableCellGroupForInstallment>
                    <SubtitleMedium>{amountFormatter(row.presentAmount)}</SubtitleMedium>
                  </TableCellGroupForInstallment>
                  <TableCellGroupForInstallment>
                    <SubtitleMedium>{getStatusLabel(row.paymentStatus)}</SubtitleMedium>
                  </TableCellGroupForInstallment>
                </TableRowStyled>
              ))}
            </TableBody>
          </TableMui>
        </TableContainer>
        <TableFooterBoxStyled>
          <Footer>
            <BodySmallRegular>
              {`Parcelas selecionadas: ${String(itemsToPay.length).padStart(1, '0')}`}
            </BodySmallRegular>
          </Footer>
          <FlexButton
            text="Pagar com Pix"
            size="small"
            startIcon={PixRounded}
            disabled={itemsToPay.length === 0}
            handleClick={handleOpenModal}
          />
        </TableFooterBoxStyled>
      </PaperStyled>

      <InstallmentsModal
        open={isModalOpen}
        setOpenModal={handleCloseModal}
        onConfirmAndOpenPix={handleConfirmInstallmentsAndOpenPix}
      />

      <PixDisplayModeModal
        open={isPixDisplayModeModalOpen}
        setOpenModal={handleClosePixDisplayModeModal}
        onConfirmAndOpenSMSConfirmation={handleConfirmPixSendingSMS}
        openQRCodePix={handleConfirmPixQRCode}
      />

      <PixSMSConfirmationModal
        open={isPixSMSConfirmationModalOpen}
        setOpenModal={handleClosePixSendingSMS}
        setOpenQRCodePix={() => {
          handleClosePixSendingSMS();
          handleConfirmPixQRCode();
        }}
      />

      <PixQRCodeModal
        open={isPixQRCodeModalOpen}
        setOpenModal={handleClosePixQRCode}
        setOpenQRCodeByEmail={() => {
          handleClosePixQRCode();
          handleConfirmPixSendingSMS();
        }}
      />

      <SuccessfulPaymentModal open={isSuccessfulPaymentModalOpen} setOpenModal={handleCloseSuccessfulPaymentModal} />

      <UnsuccessfulPaymentModal
        open={isUnsuccessfulPaymentModalOpen}
        setOpenModal={handleCloseUnsuccessfulPaymentModal}
      />
    </Fragment>
  );
};

export const InstallmentToPayTable = () => {
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;

  const { isMobile } = useBreakpoints();
  const {
    selectedInstallments,
    unpaidItems,
    item,
    itemsToPay,
    isModalOpen,
    handleCheckboxChange,
    canInstallmentBeToggled,
    handleOpenModal,
    handleCloseModal,
    isPixDisplayModeModalOpen,
    handleOpenPixDisplayModeModal,
    handleClosePixDisplayModeModal,
    handleConfirmInstallmentsAndOpenPix,
    handleClosePixSendingSMS,
    isPixSMSConfirmationModalOpen,
    handleConfirmPixSendingSMS,
    handleConfirmPixQRCode,
    isPixQRCodeModalOpen,
    handleClosePixQRCode,
    isSuccessfulPaymentModalOpen,
    handleCloseSuccessfulPaymentModal,
    isUnsuccessfulPaymentModalOpen,
    handleCloseUnsuccessfulPaymentModal,
    handleOpenSuccessfulPaymentModal,
  } = useInstallmentsLogic();

  const paymentData = useStoreSelector((store) => store.payment.paymentData);
  useEffect(() => {
    const shouldOpenSuccessModal = contractingKey === paymentData.contractingKey && paymentData.paymentReceived;

    if (shouldOpenSuccessModal && isPixQRCodeModalOpen) {
      handleOpenSuccessfulPaymentModal();
    } else {
      handleCloseSuccessfulPaymentModal();
    }
  }, [paymentData]);

  if (unpaidItems.length === 0) {
    return <NoItemsFound />;
  }

  const commonProps: InstallmentViewProps = {
    selectedInstallments,
    unpaidItems,
    item,
    itemsToPay,
    isModalOpen,
    handleCheckboxChange,
    canInstallmentBeToggled,
    handleOpenModal,
    handleCloseModal,
    getStatusLabel,
    isPixDisplayModeModalOpen,
    isPixSMSConfirmationModalOpen,
    handleOpenPixDisplayModeModal,
    handleClosePixDisplayModeModal,
    handleConfirmInstallmentsAndOpenPix,
    handleConfirmPixSendingSMS,
    handleClosePixSendingSMS,
    handleConfirmPixQRCode,
    isPixQRCodeModalOpen,
    handleClosePixQRCode,
    isSuccessfulPaymentModalOpen,
    handleCloseSuccessfulPaymentModal,
    isUnsuccessfulPaymentModalOpen,
    handleCloseUnsuccessfulPaymentModal,
    handleOpenSuccessfulPaymentModal,
  };

  return (
    <Fragment>
      {isMobile ? <InstallmentToPayMobileTable {...commonProps} /> : <DesktopInstallmentView {...commonProps} />}
    </Fragment>
  );
};
